//* 전화 응대용 — 전화 온 번호로 그 사람의 주문(만들던 것·결제한 것)을 찾아
//* 주문자 정보와 문구를 그대로 재사용하기 위한 검색 영역.
//* 부고 조회 페이지 상단과 전화주문생성 모달(정보 입력 단계) 양쪽에서 같은 컴포넌트를 쓴다.
import { FC, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { SellerCallOrderPrefill } from '@service/seller/state/sellerCallOrderPrefill.atom';
import {
  SHOP_ORDER_PHONE_SEARCH_MIN_DIGITS,
  searchShopOrderListByPhoneForSeller,
} from '@shared/api/shopOrder/shopOrder.controller';
import { ShopOrderByPhoneForSeller } from '@shared/api/shopOrder/shopOrder.interface';
import { Button } from '@shared/components/Button';
import { Icon } from '@shared/components/icons';
import { Loading } from '@shared/components/Loading';
import { TextField } from '@shared/components/TextField';
import { DeathCharacter } from '@shared/miscValues';
import { notificateError } from '@shared/plugIn/ant-notification/ant-notifiaction';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { PaymentState } from '@shared/types';
import { formatDate } from '@shared/utils/formatDate';
import { paymentStatusLabel } from '@shared/utils/orderStatusLabel';
import { Pagination } from 'antd';

//* 표기는 @shared/utils/orderStatusLabel 공용 사전을 쓰되, 이 화면에서 NotPaid는
//* "지금 전화로 이어서 만들 주문서"라는 뜻이라 그 항목만 덮는다.
const paymentLabelOverrides = { [PaymentState.NOT_PAID]: '작성중(미결제)' };

//* 한 화면에 보여줄 결과 수 (3열 × 2줄) — 나머지는 페이지네이션으로
const PHONE_LOOKUP_PAGE_SIZE = 6;

//* 아직 돈이 안 들어온 주문 = 지금 전화로 이어서 처리할 확률이 높은 건
const isOnGoing = (status: string) =>
  status === PaymentState.NOT_PAID || status === PaymentState.WAITING;

const errorMessage = (err: any, fallback: string) => {
  const status = err?.response?.status;
  if (status === 400) return err?.response?.data?.message ?? fallback;
  if (status === 401 || status === 403) return '권한이 없습니다.';
  return fallback;
};

export const feventLabelOfOrder = (shopOrder: ShopOrderByPhoneForSeller): string => {
  const fevent = shopOrder.bugo?.fevent;
  if (!fevent) return '';
  const funeralHomeName =
    fevent.funeralHomeInfoEmbed?.name ?? fevent.funeralHomeInfo?.name ?? '';
  const roomName = fevent.roomInfoEmbed?.name ?? fevent.roomInfo?.name ?? '';
  return [
    fevent.deceasedInfo?.name ? `${DeathCharacter} ${fevent.deceasedInfo.name}님` : '',
    funeralHomeName,
    roomName,
  ]
    .filter(Boolean)
    .join(' · ');
};

export const prefillOfOrder = (
  shopOrder: ShopOrderByPhoneForSeller,
): SellerCallOrderPrefill => ({
  senderName: shopOrder.deliveryDetail?.senderName ?? '',
  senderPhoneNumber: shopOrder.deliveryDetail?.senderPhoneNumber ?? '',
  senderPhrase: shopOrder.bugoDetail?.senderPhrase ?? '',
  condolencePhrase: shopOrder.bugoDetail?.condolencePhrase ?? '',
  sourceLabel: [shopOrder.deliveryDetail?.senderName, feventLabelOfOrder(shopOrder)]
    .filter(Boolean)
    .join(' / '),
});

interface CardProps {
  shopOrder: ShopOrderByPhoneForSeller;
  onSelect: (shopOrder: ShopOrderByPhoneForSeller) => void;
  selectLabel: string;
  //* 페이지에서만 — 이 주문이 걸린 상가 부고를 아래 검색표에 띄운다
  onFindBugo?: (shopOrder: ShopOrderByPhoneForSeller) => void;
}

const PhoneOrderLookupCard: FC<CardProps> = function ({
  shopOrder,
  onSelect,
  selectLabel,
  onFindBugo,
}) {
  const deliveryDetail = shopOrder.deliveryDetail;
  const bugoDetail = shopOrder.bugoDetail;
  const feventLabel = feventLabelOfOrder(shopOrder);
  const onGoing = isOnGoing(shopOrder.paymentDetail?.status);

  return (
    <div
      className={`rounded-lg border p-4 shadow-sm ${
        onGoing ? 'border-cyan-400 bg-cyan-50' : 'border-gray-300 bg-gray-50'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="w-0 flex-1 space-y-0.5">
          <p className="truncate font-medium text-gray-900">
            {deliveryDetail?.senderName || '(주문자명 없음)'}{' '}
            <span className="text-sm font-normal text-gray-600">
              {deliveryDetail?.senderPhoneNumber}
            </span>
          </p>
          {feventLabel && <p className="truncate text-sm text-gray-700">{feventLabel}</p>}
          <p className="truncate text-sm text-gray-700">
            {shopOrder.orderDetail?.shopItemEmbed?.name}
            {shopOrder.orderDetail?.shopItemEmbed?.priceRetail ? (
              <span className="text-gray-600">
                {' '}
                {shopOrder.orderDetail.shopItemEmbed.priceRetail.toLocaleString()}원
              </span>
            ) : null}
          </p>
          <p className="text-xs text-gray-500">
            {formatDate(shopOrder.paymentDetail?.requestedAt ?? shopOrder.createdAt, {
              dateSeparater: '.',
              contains: { year: false, day: true, hours: true, minutes: true },
            })}
          </p>
        </div>
        <p
          className={`shrink-0 rounded px-2 py-0.5 text-xs font-medium ${
            onGoing ? 'bg-cyan-600 text-white' : 'bg-gray-200 text-gray-700'
          }`}
        >
          {paymentStatusLabel(shopOrder.paymentDetail?.status, paymentLabelOverrides)}
        </p>
      </div>

      {(bugoDetail?.senderPhrase || bugoDetail?.condolencePhrase) && (
        <div className="mt-2 space-y-0.5 rounded border border-gray-200 bg-white p-2 text-sm">
          <p className="break-keep text-gray-900">{bugoDetail?.senderPhrase}</p>
          <p className="break-keep text-gray-600">{bugoDetail?.condolencePhrase}</p>
        </div>
      )}

      <div className="mt-3 flex justify-end gap-2">
        {onFindBugo && shopOrder.bugo?.fevent?.deceasedInfo?.name && (
          <Button
            className="button-sm border border-gray-400 bg-white text-gray-700 hover:bg-gray-100"
            onClick={() => {
              onFindBugo(shopOrder);
            }}
          >
            이 상가 찾기
          </Button>
        )}
        <Button
          className="button-sm bg-cyan-600 text-white hover:bg-cyan-700"
          onClick={() => {
            onSelect(shopOrder);
          }}
        >
          {selectLabel}
        </Button>
      </div>
    </div>
  );
};

interface Props {
  //* 카드의 주 버튼 — 모달이면 폼에 바로 채우고, 페이지면 담아뒀다가 모달에서 채운다
  onSelect: (shopOrder: ShopOrderByPhoneForSeller) => void;
  selectLabel: string;
  onFindBugo?: (shopOrder: ShopOrderByPhoneForSeller) => void;
  title: string;
  description: string;
  defaultPhoneNumber?: string;
  //* page = 넓은 폭(카드 2열) / panel = 모달 옆 좁은 칸(1열 + 결과만 스크롤)
  variant?: 'page' | 'panel';
}

/**
 * 검색줄과 결과를 따로 배치할 수 있게 조각으로 돌려준다 —
 * 부고 조회 페이지는 검색줄만 부고검색 옆칸(그리드)에 두고 결과는 그 아래 전체 폭에 깔기 때문.
 * 한 덩어리로 쓸 거면 아래 PhoneOrderLookupSection을 쓰면 된다.
 */
export const usePhoneOrderLookup = ({
  onSelect,
  selectLabel,
  onFindBugo,
  title,
  description,
  defaultPhoneNumber,
  variant = 'page',
}: Props) => {
  const isPanel = variant === 'panel';
  const { sellerRole } = useAuth();
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);
  const { MagnifyingGlassRegular } = Icon();

  const [phoneNumber, setPhoneNumber] = useState(defaultPhoneNumber ?? '');
  const [isLoading, setIsLoading] = useState(false);
  const [shopOrderList, setShopOrderList] = useState<
    ShopOrderByPhoneForSeller[] | undefined
  >(undefined);
  //* 결과가 많으면 아래 부고 검색표까지 밀려나므로 한 화면엔 6건(3열×2줄)만
  const [page, setPage] = useState(1);

  const onSearch = async () => {
    if (phoneNumber.replace(/\D/g, '').length < SHOP_ORDER_PHONE_SEARCH_MIN_DIGITS) {
      notificateError(
        notifiacationInstance,
        `번호를 ${SHOP_ORDER_PHONE_SEARCH_MIN_DIGITS}자리 이상 입력해주세요.`,
      );
      return;
    }
    setIsLoading(true);
    try {
      const orderList = (await searchShopOrderListByPhoneForSeller(
        phoneNumber,
        sellerRole(),
      )) as unknown as ShopOrderByPhoneForSeller[];
      setShopOrderList(orderList);
      setPage(1);
    } catch (err: any) {
      console.error(err);
      notificateError(notifiacationInstance, errorMessage(err, '검색에 실패했습니다.'));
    } finally {
      setIsLoading(false);
    }
  };

  //* 서버가 최신순으로 주므로 앞쪽이 방금 만든 주문. 패널(모달)은 스크롤이라 자르지 않는다.
  const pagedShopOrderList = isPanel
    ? shopOrderList ?? []
    : (shopOrderList ?? []).slice(
        (page - 1) * PHONE_LOOKUP_PAGE_SIZE,
        page * PHONE_LOOKUP_PAGE_SIZE,
      );

  //* 검색줄(제목 + 입력 + 안내)
  const searchBar = (
    <div className="space-y-3">
      {/* 페이지에서는 옆 부고검색 문구와 같은 톤(굵기 강조 없음)으로 둔다 */}
      <p className={`w-full ${isPanel ? 'font-bold' : ''}`}>
        {title}
        <span className="font-normal text-gray-500">{description}</span>
      </p>

      <div className="space-y-1.5">
        {/* 옆 부고검색 입력과 같은 컴포넌트·같은 크기로 맞춘다 */}
        <div className="flex items-center gap-2">
          <TextField
            className="w-full"
            placeholder="전화 온 번호 (뒷 4자리도 가능)"
            type="search"
            value={phoneNumber}
            onChange={(e) => {
              setPhoneNumber(e.currentTarget.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                //* 모달 안에서는 form 제출로 튀지 않게 막는다
                e.preventDefault();
                onSearch();
              }
            }}
          />
          <Button className="center-box shrink-0 gap-2" onClick={onSearch}>
            <MagnifyingGlassRegular className="wh-4" />
            <p>검색</p>
          </Button>
        </div>
        <p className="break-keep text-xs text-gray-500">
          보내는 분·받는 분 연락처로 검색합니다. 최근 20건.
        </p>
      </div>
    </div>
  );

  //* 검색 결과 (로딩·빈 결과 안내 포함)
  const searchResult = (
    <div className="space-y-3">
      {isLoading && (
        <div className="center-box">
          <Loading />
        </div>
      )}

      {!isLoading && shopOrderList && shopOrderList.length === 0 && (
        <p className="text-sm text-gray-500">해당 번호로 접수된 주문이 없습니다.</p>
      )}

      {!isLoading && shopOrderList && shopOrderList.length > 0 && (
        <>
          {!isPanel && (
            <p className="text-xs text-gray-500">
              검색 결과 {shopOrderList.length}건 · 최신순
            </p>
          )}
          <div
            className={
              isPanel
                ? 'max-h-[26rem] space-y-3 overflow-y-auto pr-1'
                : 'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3'
            }
          >
            {pagedShopOrderList.map((shopOrder) => (
              <PhoneOrderLookupCard
                key={shopOrder._id}
                shopOrder={shopOrder}
                onSelect={onSelect}
                selectLabel={selectLabel}
                onFindBugo={onFindBugo}
              />
            ))}
          </div>

          {/* 최신순이라 1페이지가 방금 것 — 아래 부고 검색표를 가리지 않게 한 화면 6건까지만 */}
          {!isPanel && shopOrderList.length > PHONE_LOOKUP_PAGE_SIZE && (
            <div className="center-box">
              <Pagination
                current={page}
                pageSize={PHONE_LOOKUP_PAGE_SIZE}
                total={shopOrderList.length}
                onChange={setPage}
                size="small"
                showSizeChanger={false}
              />
            </div>
          )}
        </>
      )}
    </div>
  );

  return { searchBar, searchResult };
};

//* 검색줄 + 결과를 한 덩어리로 (모달 안 패널용)
export const PhoneOrderLookupSection: FC<Props> = function (props: Props) {
  const { searchBar, searchResult } = usePhoneOrderLookup(props);
  return (
    <div className="space-y-3">
      {searchBar}
      {searchResult}
    </div>
  );
};
